/**
 * The review queue's calls, and the notifications it drives. Typed by the contract path; the
 * item shape is the one `lib/api` already describes, because `shown` is open in the schema.
 */
import type { Job, ReviewDecision, ReviewItem, ReviewList } from "../lib/api";
import { apiGet, apiSend } from "../lib/http";

export const REVIEW_STATES = ["pending", "approved", "rejected", "returned"] as const;
export type ReviewState = (typeof REVIEW_STATES)[number];

export const REVIEWS_KEY = ["reviews"] as const;

/** One state of the queue. The counts cover every state whatever is filtered. */
export async function fetchReviews(state: ReviewState): Promise<ReviewList> {
  return (await apiGet("/api/v1/reviews", { query: { state } })) as unknown as ReviewList;
}

export async function fetchReview(id: string): Promise<ReviewItem> {
  return (await apiGet("/api/v1/reviews/{review_id}", { path: { review_id: id } })) as unknown as ReviewItem;
}

/** The decider is never sent: the API records whoever is signed in. A blank note is left out. */
export async function decideReview(id: string, decision: ReviewDecision, note: string): Promise<ReviewItem> {
  const trimmed = note.trim();
  return (await apiSend(
    "/api/v1/reviews/{review_id}/decision",
    trimmed ? { decision, note: trimmed } : { decision },
    { path: { review_id: id } },
  )) as unknown as ReviewItem;
}

/** Job states that wait on a person. */
const WAITING = ["awaiting_review", "awaiting_confirmation", "failed"];

export interface Notifications {
  /** The bell's number. */
  count: number;
  reviews: ReviewItem[];
  jobs: Job[];
}

/**
 * What the bell shows. The queue's own pending count is used, never a recount of the items
 * listed. A UC1 job awaiting review is already in the queue as its flagged fields, so with the
 * queue read it is not counted twice; without it the job events are all there is.
 */
export function notificationsFor(jobs: Job[], list: ReviewList | null): Notifications {
  if (!list) {
    const waiting = jobs.filter((j) => WAITING.includes(j.status));
    return { count: waiting.length, reviews: [], jobs: waiting };
  }
  const waiting = jobs.filter((j) => WAITING.includes(j.status) && j.status !== "awaiting_review");
  return {
    count: (list.counts.pending ?? 0) + waiting.length,
    reviews: list.items.filter((i) => i.state === "pending"),
    jobs: waiting,
  };
}
